/**
 * sanctionsApi — the bank's own sanctions screening (pcb-retail-api, Bill 2026-10-19).
 *
 *   GET  /v3/risk/sanctions/sources    each government list: role, entries loaded, last load, freshness, recent loads
 *   POST /v3/risk/sanctions/refresh    queue a reload of every list (runs on the server, a few minutes)
 *   POST /v3/risk/sanctions/screen     screen one name the way account opening does (recorded as a Risk test)
 *   POST /v3/risk/sanctions/calibrate  catch rate on listed names + how many ordinary names reach review
 *   GET  /v3/risk/sanctions/forecast   recent Core Director OFAC hits and how each was labelled
 *
 * RiskManager role (SuperAdmin passes). Bearer-only; no function key in the bundle.
 */
import { buildHeaders, getBaseUrl, readJsonOrThrow } from './riskSettingsApi';

interface Envelope { success?: boolean; error?: string }

export interface SanctionsSource {
  readonly id: string;
  readonly label: string;
  readonly publisher: string;
  readonly role: 'decide' | 'review' | 'off';
  readonly entries: number;
  readonly lastSuccessAt: string | null;
  readonly publisherDataAsOf: string | null;
  readonly fresh: boolean;
}

export interface SanctionsLoadRun {
  readonly sourceId: string;
  readonly status: string;
  readonly startedAt: string;
  readonly finishedAt: string | null;
  readonly entryCount: number | null;
  readonly previousEntryCount: number | null;
  readonly publisherDataAsOf: string | null;
  readonly error: string | null;
  readonly triggerSource: string | null;
}

export interface SanctionsSourcesView {
  readonly authority: string;
  readonly staleAfterHours: number;
  readonly hitScore: number;
  readonly reviewScore: number;
  readonly matcherVersion: string;
  readonly indexedNames: number;
  readonly sources: ReadonlyArray<SanctionsSource>;
  readonly runs: ReadonlyArray<SanctionsLoadRun>;
}

export interface SanctionsCandidate {
  readonly sourceId: string;
  readonly sourceLabel: string;
  readonly role: string;
  readonly sourceEntryId: string;
  readonly entryType: string;
  readonly primaryName: string;
  readonly matchedName: string;
  readonly matchedKind: string;
  readonly weakAlias: boolean;
  readonly score: number;
  readonly band: string;
  readonly programs: ReadonlyArray<string>;
  readonly birthDates: ReadonlyArray<string>;
  readonly countries: ReadonlyArray<string>;
  readonly dobComparison: string | null;
  readonly countryComparison: string | null;
}

export interface SanctionsForecastHit {
  readonly aliasId: string;
  readonly aliasName: string;
  readonly matchType: string;
  readonly entityId: string | null;
  readonly assessment: string;
  readonly explanation: string;
}

export interface SanctionsScreenResult {
  readonly screeningId: string;
  readonly verdict: string;
  readonly status: string;
  readonly authority: string;
  readonly topScore: number;
  readonly summary: string;
  readonly candidates: ReadonlyArray<SanctionsCandidate>;
  readonly forecast: ReadonlyArray<SanctionsForecastHit>;
  readonly forecastPath: string;
  readonly matcherVersion: string;
}

export interface CalibrationReport {
  readonly hitScore: number;
  readonly reviewScore: number;
  readonly entriesSampled: number;
  readonly catchRates: ReadonlyArray<{ variant: string; tried: number; caught: number; catchRate: number }>;
  readonly misses: ReadonlyArray<{ variant: string; query: string; listedName: string; sourceId: string }>;
  readonly ordinaryNamesTried: number;
  readonly ordinaryNamesFlagged: number;
  readonly falseHitRate: number;
  readonly ordinaryNamesFlaggedOnExactName: number;
  readonly flaggedExamples: ReadonlyArray<string>;
}

export interface ForecastItem {
  readonly screeningId: string;
  readonly screenedAt: string;
  readonly context: string;
  readonly name: string;
  readonly verdict: string;
  readonly status: string;
  readonly forecast: { path?: string; hits?: ReadonlyArray<SanctionsForecastHit> } | null;
}

export async function loadSanctionsSources(): Promise<SanctionsSourcesView> {
  const resp = await fetch(`${getBaseUrl()}/v3/risk/sanctions/sources`, { method: 'GET', headers: await buildHeaders() });
  return readJsonOrThrow<Envelope & SanctionsSourcesView>(resp);
}

export async function refreshSanctionsLists(): Promise<void> {
  const resp = await fetch(`${getBaseUrl()}/v3/risk/sanctions/refresh`, { method: 'POST', headers: await buildHeaders(), body: '{}' });
  await readJsonOrThrow<Envelope>(resp);
}

export async function screenName(name: string, nameType: 'Indv' | 'Org', birthDate?: string, country?: string): Promise<SanctionsScreenResult> {
  const resp = await fetch(`${getBaseUrl()}/v3/risk/sanctions/screen`, {
    method: 'POST',
    headers: await buildHeaders(),
    body: JSON.stringify({ name, nameType, birthDate: birthDate || undefined, country: country || undefined }),
  });
  const body = await readJsonOrThrow<Envelope & { result: SanctionsScreenResult }>(resp);
  return body.result;
}

export async function calibrate(sample: number): Promise<{ report: CalibrationReport; elapsedMs: number; indexedNames: number }> {
  const resp = await fetch(`${getBaseUrl()}/v3/risk/sanctions/calibrate`, {
    method: 'POST',
    headers: await buildHeaders(),
    body: JSON.stringify({ sample }),
  });
  return readJsonOrThrow<Envelope & { report: CalibrationReport; elapsedMs: number; indexedNames: number }>(resp);
}

export async function loadForecast(): Promise<ReadonlyArray<ForecastItem>> {
  const resp = await fetch(`${getBaseUrl()}/v3/risk/sanctions/forecast`, { method: 'GET', headers: await buildHeaders() });
  const body = await readJsonOrThrow<Envelope & { items?: ForecastItem[] }>(resp);
  return body.items ?? [];
}

// ── Reviews: Risk clears or confirms held screenings (Bill 2026-11-02) ─────────
//   GET  /v3/risk/sanctions/reviews?status=open|decided
//   POST /v3/risk/sanctions/reviews/{screeningId}/decision  { decision: 'cleared' | 'confirmed', reason }

export type ReviewDecisionKind = 'cleared' | 'confirmed';
export type ReviewView = 'open' | 'officers' | 'decided';

export interface SanctionsReviewDecision {
  readonly decisionId: string;
  readonly screeningId: string;
  readonly decision: ReviewDecisionKind | 'escalated';
  readonly reason: string;
  readonly decidedBy: string;
  readonly decidedAt: string;
  /** officer (decided in the officer app, officer-first mode) | risk. 'escalated' decisions are the officer sending it to Risk. */
  readonly decidedAs?: string;
}

export interface SanctionsReviewItem {
  readonly screeningId: string;
  readonly cartId: string;
  readonly personKey: string | null;
  readonly nameScreened: string;
  readonly nameType: string;
  readonly screenedAt: string;
  readonly screenedBy: string | null;
  readonly context: string;
  readonly verdict: string;
  readonly topScore: number;
  readonly candidates: ReadonlyArray<SanctionsCandidate>;
  readonly forecast: ReadonlyArray<SanctionsForecastHit>;
  readonly applicantBirthYear: number | null;
  readonly applicantCountry: string | null;
  readonly branch: string | null;
  readonly officerCode: string | null;
  readonly cartStatus: string;
  readonly testApplication: boolean;
  readonly decision: SanctionsReviewDecision | null;
}

export async function loadReviews(status: ReviewView): Promise<ReadonlyArray<SanctionsReviewItem>> {
  const resp = await fetch(`${getBaseUrl()}/v3/risk/sanctions/reviews?status=${status}`, { method: 'GET', headers: await buildHeaders() });
  const body = await readJsonOrThrow<Envelope & { items?: SanctionsReviewItem[] }>(resp);
  return body.items ?? [];
}

export async function decideReview(screeningId: string, decision: ReviewDecisionKind, reason: string): Promise<SanctionsReviewDecision> {
  const resp = await fetch(`${getBaseUrl()}/v3/risk/sanctions/reviews/${encodeURIComponent(screeningId)}/decision`, {
    method: 'POST',
    headers: await buildHeaders(),
    body: JSON.stringify({ decision, reason }),
  });
  const body = await readJsonOrThrow<Envelope & { decision: SanctionsReviewDecision }>(resp);
  return body.decision;
}
